/**
 * Session Persistence Utilities
 * Manages localStorage-based path persistence with validation and expiry
 */

const EXPIRY_DAYS = 7;
const EXPIRY_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

// Valid route patterns that can be stored
const VALID_ROUTE_PATTERNS = [
  /^\/dashboard$/,
  /^\/people$/,
  /^\/my-events$/,
  /^\/connections$/,
  /^\/connection-requests$/,
  /^\/messages$/,
  /^\/jobs$/,
  /^\/jobs\/[^/]+$/,
  /^\/profile$/,
  /^\/profile\/[^/]+$/,
  /^\/settings/,
  /^\/billing$/,
  /^\/checkout$/,
  /^\/wallet$/,
  /^\/learn$/,
  /^\/learn\/[^/]+$/,
  /^\/marketplace$/,
  /^\/ats/,
  /^\/employer-setup/,
  /^\/vendor/,
  /^\/admin/,
];

interface StoredPath {
  mode: 'personal' | 'employer';
  path: string;
  timestamp: number;
}

/**
 * Check if a path matches valid route patterns
 */
function isValidRoute(path: string): boolean {
  if (!path || typeof path !== 'string') return false;
  
  // Never redirect to auth pages
  if (path === '/auth' || path === '/welcome' || path === '/') return false;
  
  return VALID_ROUTE_PATTERNS.some(pattern => pattern.test(path));
}

/**
 * Check if stored data has expired
 */
function isExpired(timestamp: number): boolean {
  return Date.now() - timestamp > EXPIRY_MS;
}

/**
 * Get and validate stored path for a user
 */
export function getValidStoredPath(userId: string): string | null {
  if (!userId) return null;

  const key = `actrus-last-visited-${userId}`;
  const stored = localStorage.getItem(key);
  
  if (!stored) return null;

  try {
    const parsed: StoredPath = JSON.parse(stored);
    
    // Validate structure
    if (!parsed.path || !parsed.timestamp) {
      console.warn('[Session] Invalid stored path structure', { userId });
      localStorage.removeItem(key);
      return null;
    }

    // Check expiry
    if (isExpired(parsed.timestamp)) {
      console.info('[Session] Stored path expired', { userId, age: Date.now() - parsed.timestamp });
      localStorage.removeItem(key);
      return null;
    }

    // Validate route
    if (!isValidRoute(parsed.path)) {
      console.warn('[Session] Invalid stored route', { userId, path: parsed.path });
      localStorage.removeItem(key);
      return null;
    }

    return parsed.path;
  } catch (e) {
    console.error('[Session] Failed to parse stored path', { userId, error: e });
    localStorage.removeItem(key);
    return null;
  }
}

/**
 * Clean up all invalid/expired localStorage entries for a user
 */
export function cleanupInvalidStoredPaths(userId: string): void {
  if (!userId) return;

  const keysToCheck = [
    `actrus-last-path-${userId}`,
    `actrus-last-visited-${userId}`,
  ];

  keysToCheck.forEach(key => {
    const value = localStorage.getItem(key);
    if (!value) return;

    try {
      // For old format (simple string path)
      if (key.includes('last-path')) {
        const path = value;
        if (!isValidRoute(path)) {
          console.info('[Session] Cleaning up invalid path', { key, path });
          localStorage.removeItem(key);
        }
      }
      
      // For new format (JSON with timestamp)
      if (key.includes('last-visited')) {
        const parsed: StoredPath = JSON.parse(value);
        if (!parsed.path || !parsed.timestamp || isExpired(parsed.timestamp) || !isValidRoute(parsed.path)) {
          console.info('[Session] Cleaning up invalid/expired visited path', { key });
          localStorage.removeItem(key);
        }
      }
    } catch (e) {
      console.error('[Session] Error during cleanup', { key, error: e });
      localStorage.removeItem(key);
    }
  });
}

/**
 * Store path with timestamp for persistence
 */
export function storeVisitedPath(userId: string, path: string, mode: 'personal' | 'employer'): void {
  if (!userId || !path || !isValidRoute(path)) return;

  const data: StoredPath = {
    mode,
    path,
    timestamp: Date.now(),
  };

  try {
    localStorage.setItem(`actrus-last-visited-${userId}`, JSON.stringify(data));
    // Also update old format for backward compatibility
    localStorage.setItem(`actrus-last-path-${userId}`, path);
  } catch (e) {
    console.error('[Session] Failed to store path', { userId, path, error: e });
  }
}

/**
 * Get safe redirect path with fallback
 */
export function getSafeRedirectPath(userId: string, role: string = 'job_seeker'): string {
  // Try to get valid stored path
  const storedPath = getValidStoredPath(userId);
  if (storedPath) {
    return storedPath;
  }

  // Fallback based on role
  return role === 'employer' ? '/ats' : '/dashboard';
}
